import React, {
  useState, useEffect, useCallback,
} from 'react';

import { Page, PageContent, PageHeader } from '@vlabs/monokit/shared/ui/page';
import { useSteppedForm } from '@vlabs/shared/hooks';
import {
  Breadcrumb, Breadcrumbs, Stepper, Modal,
} from '@vlabs/uikit';
import { useForm, FormProvider } from 'react-hook-form';
import { generatePath, useHistory } from 'react-router-dom';

import st from './OtherScenarioStepper.module.sass';
import {
  StepCommon,
  StepFaceEstimations,
  StepBodyEstimations,
  StepMatchPolicy,
  StepStoragePolicies,
  StepTagPolicy,
  StepFaceQuality,
  StepDeepfake,
  StepLiveness,
  StepPeopleCount,
  StepCallbacksPolicy,
  StepFaceOcclusion,
} from './steps';
import { routes } from '../../../../app/routes.ts';
import { CreatedHandlerForm } from '../../create-handler/CreatedHandlerForm.tsx';
import { useCreateHandlerTranslation } from '../../create-handler/i18n.ts';
import { defaultHandler, getCompatibleHandler } from '../../handlerConfig';
import { mapHandlerFormToRequest } from '../../mapHandlerFormToRequest.js';
import { useCreateHandlerMutation } from '../../store';

const steps = [
  StepCommon,
  StepFaceEstimations,
  StepLiveness,
  StepDeepfake,
  StepFaceQuality,
  StepFaceOcclusion,
  StepBodyEstimations,
  StepPeopleCount,
  StepMatchPolicy,
  StepStoragePolicies,
  StepTagPolicy,
  StepCallbacksPolicy,
];

const lpVersion = { major: 0, minor: 0, patch: 0 };

function OtherScenarioStepper() {
  const { t } = useCreateHandlerTranslation();
  const history = useHistory();

  const { mutateAsync: createHandler } = useCreateHandlerMutation();
  const [createdModalIsOpen, setCreatedModalIsOpen] = useState(false);
  const [createdHandlerId, setCreatedHandlerId] = useState();
  const [createdHandlerName, setCreatedHandlerName] = useState();
  const form = useForm({
    defaultValues: defaultHandler,
    mode: 'onChange',
  });
  const { reset, handleSubmit } = form;

  useEffect(() => {
    const handler = getCompatibleHandler(lpVersion);
    reset(handler);
  }, [reset]);

  const onSaveHandler = useCallback(async (values) => {
    const result = await createHandler({ data: mapHandlerFormToRequest(values) });

    setCreatedHandlerId(result.handler_id);
    setCreatedHandlerName(values?.description);
    setCreatedModalIsOpen(true);
  }, [createHandler]);

  const canGoNextStep = useCallback(() => !(document.querySelector('.ReactModal__Content--after-open')), []);
  const stepper = useSteppedForm({
    form,
    canGoNextStep,
    steps,
    onSubmit: onSaveHandler,
    buttonsClass: st.StepperButtons,
  });

  const onCloseCreatedModal = () => {
    setCreatedModalIsOpen(false);
    history.goBack();
  };

  return (
    <Page>
      <PageContent>
        <PageHeader
          breadcrumbs={(
            <Breadcrumbs>
              <Breadcrumb
                caption={t('breadcrumb.handlers')}
                href={generatePath(routes.handlers.all)}
              />
              <Breadcrumb caption={t('breadcrumb.new')} />
            </Breadcrumbs>
          )}
          title={t('other_scenario.creation_title')}
        />
        <FormProvider {...form}>
          <form onSubmit={handleSubmit(onSaveHandler)}>
            <Modal
              appElement={document.getElementById('root')}
              data-testid="modal.saveOtherHandler"
              isOpen={createdModalIsOpen}
              onRequestClose={onCloseCreatedModal}
              size="small"
            >
              <CreatedHandlerForm
                handlerId={createdHandlerId}
                handlerName={createdHandlerName}
              />
            </Modal>
            <Stepper
              {...stepper}
              className={st.StepperWrapper}
            >
              {steps.map((Step) => <Step key={Step.name} />)}
            </Stepper>
          </form>
        </FormProvider>
      </PageContent>
    </Page>
  );
}

export default OtherScenarioStepper;
